import React, { useEffect, useContext } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';

import Home from 'pages/Home';
import Login from 'pages/Login';
import ForgotPassword from 'pages/ForgotPassword';
import ResetPassword from 'pages/ResetPassword';
import Orders from 'pages/Orders';
import OrderDetail from 'pages/Orders/Detail';
import FundraiserList from 'pages/Fundraiser';
import FundraiserNew from 'pages/Fundraiser/New';
import FundraiserEdit from 'pages/Fundraiser/Edit';
import ProductList from 'pages/Product';
import ProductNew from 'pages/Product/New';
import Transactions from 'pages/Transactions';
import TransactionDetail from 'pages/Transactions/Detail';
import Payouts from 'pages/Payouts';
import Refunds from 'pages/Refunds';
import PayoutDetail from 'pages/Payouts/Detail';
import Disputes from 'pages/Disputes';
import DisputeDetail from 'pages/Disputes/Detail';
import Webhooks from 'pages/Webhooks';
import WebhookDetail from 'pages/Webhooks/Detail';
import Transfers from 'pages/Transfers';
import TransferDetail from 'pages/Transfers/Detail';
import Clients from 'pages/Clients';
import Companies from 'pages/Companies';
import CompanyNew from 'pages/Companies/New';
import ClientDetail from 'pages/Clients/Detail';
import Integrations from 'pages/Integrations';

import Profile from 'pages/Profile';
import Checkout from 'pages/Checkout';
import OrderSuccess from 'pages/Checkout/OrderSuccess';

import NotFound from 'components/NotFound';

import CoreRoute from 'routes/CoreRoute';
import PrivateRoute from 'routes/PrivateRoute';
import PATHS from 'routes/paths';
import {
  HAILOPAY,
  FUNDLOCAL,
  ADMIN,
  LOCAL,
  BrandContext,
} from 'utils/brand-context';

function Brand({ subdomain }) {
  const brand = useContext(BrandContext);

  useEffect(() => {
    if (
      [HAILOPAY, FUNDLOCAL, ADMIN, LOCAL].some((item) => item === subdomain)
    ) {
      document.body.classList.add(subdomain);
    }
  }, [subdomain]);

  return (
    <Router>
      <Switch>
        <Route
          path={PATHS.checkout}
          render={(routeProps) => (
            <CoreRoute title="Checkout">
              <Checkout {...routeProps} />
            </CoreRoute>
          )}
        />
        <Route
          path={PATHS.orderSuccess}
          render={(routeProps) => (
            <CoreRoute>
              <OrderSuccess {...routeProps} />
            </CoreRoute>
          )}
        />
        <PrivateRoute
          exact
          path={PATHS.profile}
          component={Profile}
          title="Profile"
        />
        {brand.hasFundraisers && (
          <PrivateRoute
            exact
            path={PATHS.orders}
            component={Orders}
            title="Donations"
          />
        )}
        {brand.hasFundraisers && (
          <PrivateRoute
            exact
            path={PATHS.orderDetail}
            component={OrderDetail}
            title="Donation Details"
          />
        )}
        {brand.hasFundraisers && (
          <PrivateRoute
            exact
            path={PATHS.fundraisers}
            component={FundraiserList}
            title="Your Fundraisers"
          />
        )}
        {brand.hasFundraisers && (
          <PrivateRoute
            exact
            path={PATHS.fundraisersNew}
            component={FundraiserNew}
            title="Create Fundraiser"
          />
        )}
        {brand.hasFundraisers && (
          <PrivateRoute
            exact
            path={PATHS.fundraisersEdit}
            component={FundraiserEdit}
          />
        )}
        {brand.hasProducts && (
          <PrivateRoute
            exact
            path={PATHS.products}
            component={ProductList}
            title="Your Products"
          />
        )}
        {brand.hasProducts && (
          <PrivateRoute
            exact
            path={PATHS.productsNew}
            component={ProductNew}
            title="Create Product"
          />
        )}
        <PrivateRoute
          exact
          path={PATHS.transactions}
          component={Transactions}
          title="Transactions"
        />
        <PrivateRoute
          exact
          path={PATHS.transactionDetail}
          component={TransactionDetail}
          title="Transaction Details"
        />
        <PrivateRoute
          exact
          path={PATHS.payouts}
          component={Payouts}
          title="Payouts"
        />
        <PrivateRoute
          exact
          path={PATHS.payoutDetail}
          component={PayoutDetail}
          title="Payout Details"
        />
        <PrivateRoute
          exact
          path={PATHS.refunds}
          component={Refunds}
          title="Refunds"
        />
        <PrivateRoute
          exact
          path={PATHS.disputes}
          component={Disputes}
          title="Disputes"
        />
        <PrivateRoute
          exact
          path={PATHS.disputeDetail}
          component={DisputeDetail}
          title="Dispute Details"
        />
        <PrivateRoute
          exact
          path={PATHS.webhooks}
          component={Webhooks}
          title="Webhooks"
        />
        <PrivateRoute
          exact
          path={PATHS.webhookDetail}
          component={WebhookDetail}
          title="Webhook Request Details"
        />
        <PrivateRoute
          exact
          path={PATHS.transfers}
          component={Transfers}
          title="Transfers"
        />
        <PrivateRoute
          exact
          path={PATHS.transferDetail}
          component={TransferDetail}
          title="Transfer Details"
        />
        <PrivateRoute
          exact
          path={PATHS.companies}
          component={Companies}
          title="Companies"
        />
        <PrivateRoute
          exact
          path={PATHS.companiesNew}
          component={CompanyNew}
          title="CompanyNew"
        />
        <PrivateRoute
          exact
          path={PATHS.clients}
          component={Clients}
          title="Clients"
        />
        <PrivateRoute
          exact
          path={PATHS.clientDetail}
          component={ClientDetail}
          title="Client Details"
        />
        <PrivateRoute
          exact
          path={PATHS.integrations}
          component={Integrations}
          title="Integrations"
        />
        {brand.hasLogin && (
          <Route
            path={PATHS.login}
            render={(routeProps) => (
              <CoreRoute title="Login">
                <Login {...routeProps} />
              </CoreRoute>
            )}
          />
        )}
        {brand.hasLogin && (
          <Route
            path={PATHS.forgotPassword}
            render={(routeProps) => (
              <CoreRoute title="Forgot your password?">
                <ForgotPassword {...routeProps} />
              </CoreRoute>
            )}
          />
        )}
        {brand.hasLogin && (
          <Route
            path={PATHS.resetPassword}
            render={(routeProps) => (
              <CoreRoute title="Reset your password">
                <ResetPassword {...routeProps} />
              </CoreRoute>
            )}
          />
        )}
        <Redirect from={PATHS.hailopayDashboard} to={PATHS.home} />
        <PrivateRoute exact path={PATHS.home} component={Home} />
        <Route path="*">
          <NotFound />
        </Route>
      </Switch>
    </Router>
  );
}

export default Brand;
